import { getOpeningPriceMarkColumns } from '@src/utils/columns';
import { buildServerDrivenPositionSizeColumnFields } from '@src/utils/columns/serverDrivenPositionSizeColumn';
import {
  getAgGridColId,
  isTalosUser,
  logger,
  useDefaultColumns,
  type Column,
  type ColumnDef,
  type Leaves,
  type Position,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag, useRollupTreeRef, useUser } from '../../../../hooks';
import { useDisplaySettings } from '../../../../providers/DisplaySettingsProvider';
import { SubAccountPositionsBlotterMode, type PositionColumn } from './../types';

type PositionLeaves = Leaves<Position>;

const DEFAULT_BLOTTER_MODE = SubAccountPositionsBlotterMode.Standard;

export const usePositionsSubAccountsColumns = (defaultColumns: PositionColumn[]): Column[] => {
  const { homeCurrency } = useDisplaySettings();
  const rollupTreeRef = useRollupTreeRef();
  const user = useUser();

  const {
    subAccountPositionsBlotterMode,
    showSubAccountPositionPnLTagColumns,
    enableSettleGroupValuesColumns,
    enableServerDrivenNotionalAmount,
    enablePositionLegacyColumn,
  } = useFeatureFlag();

  /**
   * There are three modes of operation: Spot, Derivatives, DerivativesAdvanced.
   *
   * Besides these three modes, we also have two variants of showing PnL columns: the older standard pnl columns with "lookbacks" (with a few more),
   * and new more bespoke "PnLTag" columns. These should only ever be shown in a mutually exclusive manner, hence we use `showSubAccountPositionPnLTagColumns`
   * to achieve this mutual exclusiveness.
   */

  const columnDefinitions = useMemo(() => {
    const primaryPosition = buildServerDrivenPositionSizeColumnFields<Position>({
      enableServerDrivenNotionalAmount,
      legacy: { field: 'Amount', currencyField: 'Asset' },
      server: {
        field: 'positionValue',
        currencyField: 'positionValueAsset',
        isValueAlreadyNotional: row => row.isPositionValueNotional,
      },
    });

    const priceMarkColumns = !showSubAccountPositionPnLTagColumns
      ? Object.values(
          getOpeningPriceMarkColumns<Position>({
            fieldPrefix: '',
            homeCurrency,
          })
        ).filter((col): col is ColumnDef<Position> => col != null)
      : [];

    const allColumnDefs = compact([
      {
        field: 'SubAccount',
        type: 'subAccountName',
        sortable: true,
        params: {
          rollupTree: rollupTreeRef,
        },
        enableRowGroup: true,
      },
      {
        id: 'Asset',
        field: 'Asset',
        type: 'asset',
        sortable: true,
        title: 'Instrument',
        params: {
          assetTypeField: 'AssetType' satisfies PositionLeaves,
          colorful: true,
        },
        enableRowGroup: true,
      },
      {
        id: 'Amount',
        field: primaryPosition.field,
        type: 'size',
        title: 'Position',
        sortable: true,
        aggregate: true,
        initialWidth: 150,
        params: {
          ...primaryPosition.params,
          highlightNegative: true,
          showCurrencyForNullPosition: false,
        },
      },
      enablePositionLegacyColumn
        ? {
            id: 'position-legacy',
            field: primaryPosition.legacy.field,
            type: 'size',
            title: 'Position (Legacy)',
            sortable: true,
            aggregate: true,
            initialWidth: 150,
            initialHide: true,
            params: {
              ...primaryPosition.legacy.params,
              highlightNegative: true,
              showCurrencyForNullPosition: false,
            },
          }
        : undefined,
      {
        type: 'size',
        field: 'Equivalent.Amount',
        title: `Position (${homeCurrency})`,
        params: { currencyField: 'Equivalent.Currency' satisfies PositionLeaves, highlightNegative: true },
        sortable: true,
        aggregate: true,
        description: 'Position normalized in home currency, by using mark and underlying prices.',
      },
      {
        field: 'AvgCost',
        type: 'price',
        initialWidth: 150,
        params: {
          quoteCurrencyField: 'AvgCostCurrency' satisfies PositionLeaves,
        },
        description: 'Average cost for the instrument.',
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'Equivalent.AvgCost',
        title: `Avg Cost (${homeCurrency})`,
        type: 'price',
        initialWidth: 150,
        params: {
          quoteCurrencyField: 'Equivalent.Currency' satisfies PositionLeaves,
        },
        initialHide: true,
        description: `Average cost for the instrument, converted to the user's home currency.`,
      },
      {
        field: 'MarkPrice',
        type: 'price',
        initialWidth: 110,
        params: {
          quoteCurrencyField: 'MarkPriceCurrency' satisfies PositionLeaves,
        },
        description: 'Most recent mark price for the instrument.',
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'Equivalent.MarkPrice',
        type: 'price',
        title: `Mark Price (${homeCurrency})`,
        initialWidth: 130,
        params: {
          quoteCurrencyField: 'Equivalent.Currency' satisfies PositionLeaves,
        },
        initialHide: true,
        description: `Most recent mark price for the instrument, converted to the user's home currency.`,
      },
      {
        field: 'Delta',
        type: 'delta',
        aggregate: true,
        initialWidth: 150,
        params: {
          currencyField: 'DeltaCurrency' satisfies PositionLeaves,
        },
        description:
          "Delta measures the rate of change of an instrument's price with respect to changes in its underlying asset's price.",
      },
      {
        field: 'Equivalent.Delta',
        type: 'size',
        title: `Delta (${homeCurrency})`,
        aggregate: true,
        initialWidth: 150,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description:
          "Delta measures the rate of change of an instrument's price with respect to changes in its underlying asset's price, in home currency.",
      },
      enableSettleGroupValuesColumns && {
        field: 'SettleGroupValues.UnrealizedPnL',
        initialWidth: 180, // a bit bigger for the title to fit nicely
        type: 'size',
        title: 'Unrealized Delta Impact',
        params: {
          currencyField: 'SettleGroupAsset' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description:
          'Unrealized P&L from open derivative positions, in settlement currency (e.g. BTC for coin-margined perps).',
      },
      enableSettleGroupValuesColumns && {
        field: 'SettleGroupValues.RealizedPnL',
        initialWidth: 180, // a bit bigger for the title to fit nicely
        type: 'size',
        title: 'Realized Delta Impact',
        params: {
          currencyField: 'SettleGroupAsset' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: 'Realized P&L from closed derivative positions, in settlement currency.',
      },
      enableSettleGroupValuesColumns && {
        field: 'SettleGroupValues.FundingPnL',
        initialWidth: 180, // a bit bigger for the title to fit nicely
        type: 'size',
        title: 'Funding Delta Impact',
        params: {
          currencyField: 'SettleGroupAsset' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: 'Net funding paid or received on perpetual contracts, in settlement currency.',
      },
      enableSettleGroupValuesColumns && {
        field: 'SettleGroupValues.CumFees',
        initialWidth: 180, // a bit bigger for the title to fit nicely
        type: 'size',
        title: 'Fees Delta Impact',
        params: {
          currencyField: 'SettleGroupAsset' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description:
          'Exchange trading fees paid when entering or exiting derivative positions, in settlement currency.',
      },
      enableSettleGroupValuesColumns && {
        field: 'SettleGroupValues.Total',
        initialWidth: 180, // a bit bigger for the title to fit nicely
        type: 'size',
        title: 'Total Delta Impact',
        params: {
          currencyField: 'SettleGroupAsset' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description:
          'Total impact of Unrealized, Realized, Funding, and Fees from derivative positions, in base/settlement currency.',
      },
      {
        field: 'Equivalent.CumFees',
        type: 'size',
        title: `Fees (${homeCurrency})`,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
        },
        aggregate: true,
        description: 'Cumulative trading fees paid (or rebates received) in home currency.',
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'Equivalent.UnrealizedPnL',
        type: 'size',
        title: `Unrealized PnL (${homeCurrency})`,
        aggregate: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: 'Cumulative unrealized P&L in home currency.',
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'Equivalent.RealizedPnL',
        type: 'size',
        title: `Realized PnL (${homeCurrency})`,
        aggregate: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: 'Cumulative realized P&L in home currency.',
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'TotalPnL',
        type: 'size',
        title: 'Total LTD PnL',
        aggregate: true,
        params: {
          currencyField: 'PnLCurrency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: 'Sum of Unrealized, Realized and Funding P&Ls, net of Fees.',
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'Equivalent.TotalPnL',
        type: 'size',
        title: `Total LTD PnL (${homeCurrency})`,
        aggregate: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: 'Sum of Unrealized, Realized and Funding P&Ls, net of Fees. In home currency.',
      },

      // Hidden by default columns below
      {
        id: 'underlying' satisfies ExtraSubAccountPositionColumnIds,
        type: 'positionUnderlyingAsset',
        title: 'Underlying',
        enableRowGroup: true,
        initialHide: true,
        description: "Underlying of asset upon which a derivative's price is based.",
      },
      {
        field: 'AssetType',
        title: 'Product Type',
        type: 'productTypeField',
        initialWidth: 130,
        enableRowGroup: true,
        initialHide: true,
      },
      {
        field: 'CumFees',
        type: 'size',
        title: 'Fees',
        params: {
          currencyField: 'PnLCurrency' satisfies PositionLeaves,
        },
        aggregate: true,
        initialHide: true,
        description: `Cumulative trading fees paid (or rebates received) (in settlement currency for derivatives, home currency otherwise)`,
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'UnrealizedPnL',
        type: 'size',
        title: 'Unrealized PnL',
        aggregate: true,
        params: {
          currencyField: 'PnLCurrency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        initialHide: true,
        description: 'Cumulative unrealized P&L.',
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'RealizedPnL',
        type: 'size',
        title: 'Realized PnL',
        params: {
          currencyField: 'PnLCurrency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        initialHide: true,
        aggregate: true,
        description: 'Cumulative realized P&L.',
      },
      {
        field: 'Amount',
        id: 'position-qty' satisfies ExtraSubAccountPositionColumnIds,
        type: 'size',
        title: 'Position Qty',
        description: 'Number of derivative contracts or size of spot position.',
        params: {
          showInTermsOfContracts: true,
          highlightNegative: true,
          currencyField: 'Asset' satisfies PositionLeaves,
          trimTrailingZeroes: true,
        },
        initialHide: true,
      },
      {
        field: 'Gamma',
        type: 'gamma',
        initialWidth: 80,
        initialHide: true,
        description:
          "Gamma measures the rate of change of option's delta with respect to changes in the underlying asset's price.",
      },
      {
        field: 'Vega',
        type: 'vega',
        initialWidth: 60,
        initialHide: true,
        description:
          "Vega measures the rate of change (in USD) of an option's price in response to a +1 vol point move in implied volatility.",
      },
      {
        field: 'Theta',
        type: 'theta',
        initialWidth: 60,
        initialHide: true,
        description:
          'Theta measures the change (in USD) in the price of an option for a one-day decrease in its time to expiration. ',
      },
      {
        field: 'Rho',
        type: 'rho',
        initialWidth: 60,
        initialHide: true,
        description:
          "Rho measures the rate of change (in USD) of an option's price in response to a 1% shift in interest rate.",
      },
      {
        field: 'OutstandingBuy',
        type: 'size',
        params: { currencyField: 'Asset' satisfies PositionLeaves, showCurrencyForNullPosition: false },
        initialHide: true,
      },
      {
        field: 'OutstandingSell',
        type: 'size',
        params: { currencyField: 'Asset' satisfies PositionLeaves, showCurrencyForNullPosition: false },
        initialHide: true,
      },
      {
        type: 'size',
        field: 'Equivalent.OutstandingBuy',
        title: `Outstanding Buy (${homeCurrency})`,
        params: { currencyField: 'Equivalent.Currency' satisfies PositionLeaves, highlightNegative: true },
        sortable: true,
        aggregate: true,
        initialHide: true,
      },
      {
        type: 'size',
        field: 'Equivalent.OutstandingSell',
        title: `Outstanding Sell (${homeCurrency})`,
        params: { currencyField: 'Equivalent.Currency' satisfies PositionLeaves, highlightNegative: true },
        sortable: true,
        aggregate: true,
        initialHide: true,
      },
      {
        field: 'LastUpdateTime',
        type: 'date',
        initialHide: true,
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'PnLLookbacks.H24.Equivalent.PnLDelta',
        type: 'size',
        title: '24H PnL',
        aggregate: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        initialHide: true,
        description: 'P&L on assets over the 24 hours to the current price mark.',
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'PnLLookbacks.D7.Equivalent.PnLDelta',
        type: 'size',
        title: '7D PnL',
        aggregate: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        initialHide: true,
        description: 'P&L on assets over the past 7 days to the current price mark.',
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'PnLLookbacks.D30.Equivalent.PnLDelta',
        type: 'size',
        title: '30D PnL',
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: 'P&L on asset over the past 30 days to the current price mark.',
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'PnLLookbacks.D365.Equivalent.PnLDelta',
        type: 'size',
        title: '365D PnL',
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: 'P&L on asset over the past 365 days to the current price mark.',
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'PnLLookbacks.Today.Equivalent.PnLDelta',
        type: 'size',
        title: 'Intraday PnL',
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: "P&L on asset from start of day to the current price mark. (e.g. today's gain/loss)",
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'PnLLookbacks.WeekToDate.Equivalent.PnLDelta',
        type: 'size',
        title: 'WTD PnL',
        aggregate: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        initialHide: true,
        description: 'P&L on asset from start of day on Monday to the current price mark.',
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'PnLLookbacks.MonthToDate.Equivalent.PnLDelta',
        type: 'size',
        title: 'MTD PnL',
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: 'P&L on asset from start of day on 1st of the month to the current price mark.',
      },
      !showSubAccountPositionPnLTagColumns && {
        field: 'PnLLookbacks.YearToDate.Equivalent.PnLDelta',
        type: 'size',
        title: 'YTD PnL',
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: 'P&L on asset from start of day on 1st of the year to the current price mark.',
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'PnLTags.EndOfDay.OpeningPosition',
        type: 'size',
        title: 'EOD Position',
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Asset' satisfies PositionLeaves,
          highlightNegative: true,
          showCurrencyForNullPosition: false,
        },
        description: 'Notional position as of EOD timestamp.',
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'PnLTags.EndOfDay.Equivalent.OpeningPosition',
        type: 'size',
        title: `EOD Position (${homeCurrency})`,
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: "Notional position as of EOD timestamp, converted to the user's home currency",
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'eodPositionUsdEodMark',
        type: 'size',
        title: `EOD Position (${homeCurrency}) (EOD MARK)`,
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'MarkPriceCurrency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: `End-of-day position value in ${homeCurrency}, calculated using the (${homeCurrency}) Mark price.`,
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'PnLTags.EndOfDay.OpeningPriceMark',
        type: 'price',
        title: 'EOD Mark Price',
        initialHide: true,
        params: {
          quoteCurrencyField: 'MarkPriceCurrency' satisfies PositionLeaves,
        },
        description: 'End of day price mark.',
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'PnLTags.EndOfDay.Equivalent.OpeningPriceMark',
        type: 'price',
        title: `EOD Mark Price (${homeCurrency})`,
        initialHide: true,
        params: {
          quoteCurrencyField: 'Equivalent.Currency' satisfies PositionLeaves,
        },
        description: `End of day price mark, converted to the user's home currency.`,
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'PnLTags.EndOfDay.OpenTimestamp',
        type: 'date',
        title: 'EOD Mark Time',
        initialHide: true,
        description: 'End of day price mark timestamp.',
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'PnLTags.EndOfDay.OpeningUnrealizedPnL',
        type: 'size',
        title: 'EOD Position PnL',
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'PnLCurrency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description:
          'PnL of the EOD notional position. Computed by marking the EOD position to current price and comparing to EOD price mark.',
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'PnLTags.EndOfDay.Equivalent.OpeningUnrealizedPnL',
        type: 'size',
        title: `EOD Position PnL (${homeCurrency})`,
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: "PnL of the EOD notional position, converted to the user's home currency.",
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'PnLTags.EndOfDay.TradePositionDelta',
        type: 'size',
        title: 'Day Trade Position',
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Asset' satisfies PositionLeaves,
          highlightNegative: true,
          showCurrencyForNullPosition: false,
        },
        description: 'Current notional position minus EOD notional position.',
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'PnLTags.EndOfDay.Equivalent.TradePositionDelta',
        type: 'size',
        title: `Day Trade Position (${homeCurrency})`,
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Asset' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: "Current notional position minus EOD notional position, converted to the user's home currency",
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'PnLTags.EndOfDay.TradeUnrealizedPnL',
        type: 'size',
        title: 'Day Trade PnL',
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'PnLCurrency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description:
          "PnL attributable to current day's trading activity. Computed by summing up the PnL of trades executed today, which is computed by comparing the current price mark against the traded price.",
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'PnLTags.EndOfDay.Equivalent.TradeUnrealizedPnL',
        type: 'size',
        title: `Day Trade PnL (${homeCurrency})`,
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: "PnL attributable to current day's trading activity, converted to the user's home currency.",
      },
      // This is an explicit 'as any' that needs more time, while the columns are consistently built above,
      // - Alternatively we can move columns here to a mapping model where this mapping is used elsewhere
      ...(priceMarkColumns as any),
      showSubAccountPositionPnLTagColumns && {
        field: 'eodTotalPnL',
        type: 'size',
        title: 'Total PnL',
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'PnLCurrency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: 'Sum EOD Position PnL and Day Trade PnL.',
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'eodTotalPnLEquivalent',
        type: 'size',
        title: `Total PnL (${homeCurrency})`,
        aggregate: true,
        initialHide: true,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        description: "Sum EOD Position PnL and Day Trade PnL, converted to the user's home currency.",
      },
      showSubAccountPositionPnLTagColumns && {
        field: 'Equivalent.PnLConversionRate',
        type: 'price',
        title: 'Quote Ccy Rate',
        initialHide: true,
        params: {
          quoteCurrencyField: 'Equivalent.Currency' satisfies PositionLeaves,
        },
        description: "Quoted currency to user's home currency conversion rate.",
      },
      {
        field: 'FundingPnL',
        type: 'size',
        title: 'Funding PnL',
        aggregate: true,
        params: {
          currencyField: 'FundingPnLCurrency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        initialHide: true,
        description: 'Cumulative funding payment paid or received in settlement terms.',
      },
      {
        field: 'Equivalent.FundingPnL',
        type: 'size',
        title: `Funding PnL (${homeCurrency})`,
        params: {
          currencyField: 'Equivalent.Currency' satisfies PositionLeaves,
          highlightNegative: true,
        },
        aggregate: true,
        initialHide: true,
        description: 'Cumulative funding payment paid or received in home currency.',
      },
      {
        field: 'AvgCostCurrency',
        type: 'currency',
        initialHide: true,
      },
      {
        field: 'PnLCurrency',
        type: 'currency',
        title: 'PnL Currency',
        initialHide: true,
      },
      isTalosUser(user) && {
        field: 'Status',
        type: 'text',
        initialHide: true,
      },
    ]);

    const columnDefsFilteredToBlotterMode = filterColumnsByMode(
      subAccountPositionsBlotterMode ?? DEFAULT_BLOTTER_MODE,
      allColumnDefs
    );
    return new Map<string, Column>(columnDefsFilteredToBlotterMode.map(c => [getAgGridColId(c), c]));
  }, [
    homeCurrency,
    rollupTreeRef,
    user,
    subAccountPositionsBlotterMode,
    showSubAccountPositionPnLTagColumns,
    enableSettleGroupValuesColumns,
    enableServerDrivenNotionalAmount,
    enablePositionLegacyColumn,
  ]);

  return useDefaultColumns(defaultColumns, columnDefinitions);
};

// Its not much but its honest work
type ExtraSubAccountPositionColumnIds = 'underlying' | 'position-qty' | 'position-legacy';

const standardColumns = [
  'SubAccount',
  'Asset',
  'Amount',
  'position-legacy',
  'Equivalent.Amount',
  'AvgCost',
  'Equivalent.AvgCost',
  'MarkPrice',
  'Equivalent.MarkPrice',
  'Delta',
  'Equivalent.Delta',
  'SettleGroupValues.UnrealizedPnL',
  'SettleGroupValues.RealizedPnL',
  'SettleGroupValues.FundingPnL',
  'SettleGroupValues.CumFees',
  'SettleGroupValues.Total',
  'Equivalent.CumFees',
  'Equivalent.UnrealizedPnL',
  'Equivalent.RealizedPnL',
  'Equivalent.FundingPnL',
  'Equivalent.TotalPnL',
  'underlying',
  'AssetType',
  'CumFees',
  'UnrealizedPnL',
  'RealizedPnL',
  'FundingPnL',
  'position-qty',
  'OutstandingBuy',
  'OutstandingSell',
  'Equivalent.OutstandingBuy',
  'Equivalent.OutstandingSell',
  'PnLLookbacks.H24.Equivalent.PnLDelta',
  'PnLLookbacks.D7.Equivalent.PnLDelta',
  'PnLLookbacks.D30.Equivalent.PnLDelta',
  'PnLLookbacks.D365.Equivalent.PnLDelta',
  'PnLLookbacks.Today.Equivalent.PnLDelta',
  'PnLLookbacks.WeekToDate.Equivalent.PnLDelta',
  'PnLLookbacks.MonthToDate.Equivalent.PnLDelta',
  'PnLLookbacks.YearToDate.Equivalent.PnLDelta',
  'PnLTags.EndOfDay.OpeningPosition',
  'PnLTags.EndOfDay.Equivalent.OpeningPosition',
  'PnLTags.EndOfDay.OpeningPriceMark',
  'PnLTags.EndOfDay.Equivalent.OpeningPriceMark',
  'PnLTags.EndOfDay.OpenTimestamp',
  'PnLTags.EndOfDay.OpeningUnrealizedPnL',
  'PnLTags.EndOfDay.Equivalent.OpeningUnrealizedPnL',
  'PnLTags.EndOfDay.TradePositionDelta',
  'PnLTags.EndOfDay.Equivalent.TradePositionDelta',
  'PnLTags.EndOfDay.TradeUnrealizedPnL',
  'PnLTags.EndOfDay.Equivalent.TradeUnrealizedPnL',
  'PnLLookbacks.H24.OpeningPriceMark',
  'PnLLookbacks.H24.Equivalent.OpeningPriceMark',
  'PnLLookbacks.H24.OpenTimestamp',
  'PnLLookbacks.D7.OpeningPriceMark',
  'PnLLookbacks.D7.Equivalent.OpeningPriceMark',
  'PnLLookbacks.D7.OpenTimestamp',
  'PnLLookbacks.D30.OpeningPriceMark',
  'PnLLookbacks.D30.Equivalent.OpeningPriceMark',
  'PnLLookbacks.D30.OpenTimestamp',
  'PnLLookbacks.D365.OpeningPriceMark',
  'PnLLookbacks.D365.Equivalent.OpeningPriceMark',
  'PnLLookbacks.D365.OpenTimestamp',
  'PnLLookbacks.Today.OpeningPriceMark',
  'PnLLookbacks.Today.Equivalent.OpeningPriceMark',
  'PnLLookbacks.Today.OpenTimestamp',
  'PnLLookbacks.WeekToDate.OpeningPriceMark',
  'PnLLookbacks.WeekToDate.Equivalent.OpeningPriceMark',
  'PnLLookbacks.WeekToDate.OpenTimestamp',
  'PnLLookbacks.YearToDate.OpeningPriceMark',
  'PnLLookbacks.YearToDate.Equivalent.OpeningPriceMark',
  'PnLLookbacks.YearToDate.OpenTimestamp',
  'eodTotalPnL',
  'eodTotalPnLEquivalent',
  'eodPositionUsdEodMark',
  'Equivalent.PnLConversionRate',
  'LastUpdateTime',
  'Status',
] as const satisfies (Leaves<Position> | ExtraSubAccountPositionColumnIds)[];
const standardColumnsSet = new Set<string>(standardColumns);

const advancedColumns = [
  // the advanced columns are purely additive
  ...standardColumns,
  'Gamma',
  'Vega',
  'Theta',
  'Rho',
  'AvgCostCurrency',
  'PnLCurrency',
] as const satisfies (Leaves<Position> | ExtraSubAccountPositionColumnIds)[];
const advancedColumnsSet = new Set<string>(advancedColumns);

function filterColumnsByMode(mode: string, columnDefs: ColumnDef<Position>[]): ColumnDef<Position>[] {
  let allowedColumns =
    mode === SubAccountPositionsBlotterMode.Standard
      ? standardColumnsSet
      : mode === SubAccountPositionsBlotterMode.Advanced
        ? advancedColumnsSet
        : undefined;

  if (!allowedColumns) {
    // As of 2.49 we migrate from the old Spot, Derivatives, DerivativesAdvanced modes to just Standard and Advanced. These are org configs.
    // To ease the burden on CS, we do some reasonable defaulting here. Remove for 2.50.
    if (mode === 'Spot' || mode === 'Derivatives') {
      allowedColumns = standardColumnsSet;
    } else if (mode === 'DerivativesAdvanced') {
      allowedColumns = advancedColumnsSet;
    } else {
      // undefined or just incorrect
      allowedColumns = standardColumnsSet;
    }

    logger.error(
      new Error(
        `Misconfigured SubAccountPositionsBlotter mode ${mode}. The client is being defaulted columns. We should change the client's mode.`
      )
    );
  }

  return columnDefs.filter(def => allowedColumns?.has(getAgGridColId(def)));
}
